import { getHotelsData } from '@/app/api/hotels'
import {
	DestPagesProps,
	HotelsDataType,
	PaginatedHotelsResponse,
	QueryPageParam,
	UserData,
} from '@/app/types'
import { useInfiniteQuery } from '@tanstack/react-query'
import { omit } from 'lodash'

interface UseFallbackHotelsQueryProps {
	props: DestPagesProps
	shouldRunFallbackQuery: boolean
	startTimeRef: React.MutableRefObject<number>
	setIsFallbackFetchingComplete: (value: boolean) => void
	userData: UserData | null
	mapQueryKey?: string
}

export const useFallbackHotelsQuery = ({
	props,
	shouldRunFallbackQuery,
	startTimeRef,
	setIsFallbackFetchingComplete,
	userData,
	mapQueryKey,
}: UseFallbackHotelsQueryProps) => {
	const fallbackQueryKey = [
		'fallbackHotels',
		props.params.lang,
		props.params.dest.join(','),
		mapQueryKey ?? ''
	]

	return useInfiniteQuery<
		PaginatedHotelsResponse,
		Error,
		HotelsDataType,
		string[],
		QueryPageParam
	>({
		queryKey: fallbackQueryKey,
		queryFn: ({ pageParam = { page: 1, offset: 0 } }) =>
			getHotelsData({
				...props,
				searchParams: {
					...omit(props.searchParams, [
						's',
						'gr',
						'mn',
						'mx',
						'fa',
						'p',
						't',
						'room',
						'ap',
					]),
					...(mapQueryKey ? { polygon: mapQueryKey } : {}),
					room: ['2'],
					page: String(pageParam.page),
					userAgent: userData?.userAgent,
					sessionID: userData?.sessionID,
					// If no currency in URL, use local currency from userData
					currency: props.searchParams.currency || userData?.defaultCurrency?.abbreviation,
				},
			}).then((response) => {
				return {
					...response,
					page: pageParam.page,
					offset: pageParam.offset,
				}
			}),
		getNextPageParam: (lastPage, allPages) => {
			if (lastPage.page < lastPage.pages) {
				return {
					page: lastPage.page + 1,
					offset: lastPage?.offset
						? lastPage?.offset + lastPage.hotels.length
						: 0,
				}
			}
			return undefined
		},
		refetchInterval: (queryInfo) => {
			if (
				queryInfo.state.data?.pages[queryInfo.state.data.pages.length - 1]
					?.complete
			) {
				setIsFallbackFetchingComplete(true)
				return false
			}

			const timeElapsed = Date.now() - startTimeRef.current
			if (timeElapsed > 20000) {
				setIsFallbackFetchingComplete(true)
				return false
			}

			return 600
		},
		initialPageParam: { page: 1, offset: 0 },
		enabled: !!userData && shouldRunFallbackQuery,
	})
}
